"use client";

import Link from "next/link";
import { useLang } from "@/contexts/language-context";
import AnimatedSection, { AnimatedElement } from "./AnimatedSection";
import {
  Utensils,
  Wine,
  ArrowRight,
  Check,
  Minus,
} from "lucide-react";

type Row = {
  label: { en: string; es: string };
  restaurant: { en: string; es: string } | "yes" | "no";
  nightlife: { en: string; es: string } | "yes" | "no";
};

const ROWS: Row[] = [
  {
    label: { en: "Who it's built for", es: "Para quién está hecho" },
    restaurant: { en: "Restaurants · Food trucks · QSR", es: "Restaurantes · Food trucks · QSR" },
    nightlife: { en: "Bars · Nightclubs · Live events", es: "Bares · Clubs · Eventos en vivo" },
  },
  {
    label: { en: "Front of house", es: "Front of house" },
    restaurant: { en: "Rush-hour POS", es: "POS de rush hour" },
    nightlife: { en: "VIP Table Map + bar tabs", es: "VIP Table Map + bar tabs" },
  },
  {
    label: { en: "Back of house", es: "Back of house" },
    restaurant: { en: "KDS · Inventory · COGS", es: "KDS · Inventario · COGS" },
    nightlife: { en: "Bottle service · Min tracking", es: "Bottle service · Tracking de mínimos" },
  },
  {
    label: { en: "How customers order", es: "Cómo ordenan los clientes" },
    restaurant: { en: "Online ordering · Kiosk · Website", es: "Pedidos online · Kiosco · Sitio web" },
    nightlife: { en: "Event ticketing · Venue microsite", es: "Ticketing de eventos · Microsite" },
  },
  {
    label: { en: "Door / entrance", es: "Puerta / entrada" },
    restaurant: "no",
    nightlife: { en: "QR check-in · Duplicate detection", es: "Check-in QR · Detección de duplicados" },
  },
  {
    label: { en: "Marketing / loyalty", es: "Marketing / lealtad" },
    restaurant: { en: "Loyalty CRM · SMS receipts", es: "Lealtad CRM · SMS de recibos" },
    nightlife: { en: "Promoter links · Guest CRM", es: "Links de promotores · Guest CRM" },
  },
  {
    label: { en: "Dual pricing · Cash discount", es: "Dual pricing · Descuento en efectivo" },
    restaurant: "yes",
    nightlife: "yes",
  },
  {
    label: { en: "Offline cash sales", es: "Ventas en efectivo offline" },
    restaurant: "yes",
    nightlife: "yes",
  },
  {
    label: { en: "Starting plan", es: "Plan inicial" },
    restaurant: { en: "Start $0 (with Redy payments) · Pro $97/mo", es: "Start $0 (con pagos Redy) · Pro $97/mes" },
    nightlife: { en: "Nightlife $147/mo · PRO $197/mo", es: "Nightlife $147/mes · PRO $197/mes" },
  },
];

function Cell({
  value,
  tone,
}: {
  value: Row["restaurant"] | Row["nightlife"];
  tone: "restaurant" | "nightlife";
}) {
  const { t } = useLang();

  if (value === "yes") {
    return (
      <div className="flex items-center gap-2">
        <div
          className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${
            tone === "restaurant" ? "bg-redy-orange/15" : "bg-[#7C3AED]/20"
          }`}
        >
          <Check
            className="w-3 h-3 stroke-[3]"
            style={{ color: tone === "restaurant" ? "#F58A1F" : "#C4B5FD" }}
          />
        </div>
        <span className="text-[13px] font-bold text-white/85">
          {t("Included", "Incluido")}
        </span>
      </div>
    );
  }

  if (value === "no") {
    return (
      <div className="flex items-center gap-2">
        <div className="w-5 h-5 rounded-full bg-white/5 flex items-center justify-center shrink-0">
          <Minus className="w-3 h-3 text-white/30" />
        </div>
        <span className="text-[13px] font-medium text-white/30">
          {t("Not in this vertical", "No aplica")}
        </span>
      </div>
    );
  }

  return (
    <span className="text-[13px] font-bold text-white/85 leading-snug">
      {t(value.en, value.es)}
    </span>
  );
}

/**
 * "Two businesses, one platform" — bridge section that introduces nightlife
 * on the home page and shows side-by-side how the same Redy account adapts
 * to a restaurant by day and a venue by night. Designed to sit between the
 * features bento and the by-the-numbers section.
 */
export default function TwoVerticalsSection() {
  const { t } = useLang();

  return (
    <section
      id="verticals"
      className="relative py-32 px-6 overflow-hidden bg-redy-charcoal text-white"
    >
      {/* Atmosphere — split warm orange (left) / cool violet (right) */}
      <div className="absolute top-1/3 -left-32 w-[640px] h-[640px] bg-redy-orange/15 rounded-full blur-[160px] pointer-events-none" />
      <div className="absolute bottom-0 -right-32 w-[640px] h-[640px] bg-[#7C3AED]/20 rounded-full blur-[160px] pointer-events-none" />

      <div className="relative max-w-[1280px] mx-auto">
        {/* Eyebrow + headline */}
        <AnimatedSection className="text-center mb-20">
          <div className="text-[11px] font-bold tracking-widest uppercase text-redy-orange mb-4">
            {t("Pick your vertical", "Elige tu vertical")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-tight leading-[1.02]">
            {t("Built differently", "Hecho distinto")}
            <br />
            <span
              className="bg-clip-text text-transparent"
              style={{
                backgroundImage:
                  "linear-gradient(135deg, #FB923C 0%, #F472B6 55%, #C4B5FD 100%)",
              }}
            >
              {t("for two kinds of business.", "para dos tipos de negocio.")}
            </span>
          </h2>
          <p className="text-[15px] md:text-[17px] font-medium text-white/55 max-w-[640px] mx-auto mt-6 leading-relaxed">
            {t(
              "Restaurants and venues don't run the same way — so we built two products on one platform. Pick yours below and we'll show you the full stack.",
              "Restaurantes y venues no operan igual — por eso construimos dos productos sobre una plataforma. Elige el tuyo y te mostramos el stack completo."
            )}
          </p>
        </AnimatedSection>

        {/* Two cards side by side */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
          {/* Restaurant card */}
          <AnimatedElement
            delay={0.05}
            className="relative bg-white/[0.04] border border-white/10 rounded-[32px] p-8 md:p-10 overflow-hidden hover:border-redy-orange/30 transition-all"
          >
            <div className="absolute top-0 right-0 w-[280px] h-[280px] bg-redy-orange/10 rounded-full blur-[80px] pointer-events-none" />
            <div className="relative">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-[14px] bg-redy-orange/15 flex items-center justify-center">
                  <Utensils className="w-6 h-6 text-redy-orange" />
                </div>
                <div className="text-[11px] font-bold tracking-widest uppercase text-redy-orange/80">
                  {t("Restaurant", "Restaurante")}
                </div>
              </div>
              <h3 className="text-[28px] md:text-[34px] font-black tracking-tight leading-tight mb-4">
                {t("Run a kitchen.", "Maneja una cocina.")}
                <br />
                <span className="text-white/40">
                  {t("Keep more of every sale.", "Quédate con más de cada venta.")}
                </span>
              </h3>
              <p className="text-[14px] font-medium text-white/55 leading-relaxed mb-6">
                {t(
                  "POS, KDS, kiosk, online ordering, website, inventory, loyalty and SMS. Built for service speed and dual pricing — the card price is designed to cover the processing cost.",
                  "POS, KDS, kiosco, pedidos online, sitio web, inventario, lealtad y SMS. Hecho para velocidad de servicio y dual pricing — el precio con tarjeta está pensado para cubrir el costo de procesamiento."
                )}
              </p>

              {/* Tag pills */}
              <div className="flex flex-wrap gap-2 mb-8">
                {[
                  { en: "Rush-hour POS", es: "POS de rush hour" },
                  { en: "KDS routing", es: "Ruteo KDS" },
                  { en: "Online ordering", es: "Pedidos online" },
                  { en: "Inventory + COGS", es: "Inventario + COGS" },
                  { en: "12 features", es: "12 features" },
                ].map((tag) => (
                  <span
                    key={tag.en}
                    className="text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full bg-redy-orange/10 text-redy-orange/90 border border-redy-orange/20"
                  >
                    {t(tag.en, tag.es)}
                  </span>
                ))}
              </div>

              <Link
                href="/solution/restaurants"
                className="inline-flex items-center gap-2 text-[14px] font-bold text-redy-charcoal bg-redy-orange px-6 py-3 rounded-full hover:scale-[1.02] transition-all shadow-lg shadow-redy-orange/20"
              >
                {t("Explore restaurant", "Ver restaurant")}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </AnimatedElement>

          {/* Nightlife card */}
          <AnimatedElement
            delay={0.15}
            className="relative bg-white/[0.04] border border-white/10 rounded-[32px] p-8 md:p-10 overflow-hidden hover:border-[#7C3AED]/40 transition-all"
          >
            <div className="absolute top-0 right-0 w-[280px] h-[280px] bg-[#7C3AED]/20 rounded-full blur-[80px] pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-[200px] h-[200px] bg-[#F472B6]/15 rounded-full blur-[80px] pointer-events-none" />
            <div className="relative">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-[14px] bg-[#7C3AED]/20 flex items-center justify-center">
                  <Wine className="w-6 h-6" style={{ color: "#C4B5FD" }} />
                </div>
                <div
                  className="text-[11px] font-bold tracking-widest uppercase"
                  style={{ color: "#F472B6" }}
                >
                  {t("Nightlife", "Nightlife")}
                </div>
              </div>
              <h3 className="text-[28px] md:text-[34px] font-black tracking-tight leading-tight mb-4">
                {t("Run the night.", "Maneja la noche.")}
                <br />
                <span className="text-white/40">
                  {t("Ticket. Door. Bar. VIP.", "Ticket. Puerta. Bar. VIP.")}
                </span>
              </h3>
              <p className="text-[14px] font-medium text-white/55 leading-relaxed mb-6">
                {t(
                  "Event ticketing, QR door check-in, live bar tabs, VIP table map and promoter links. Replace a ticketing site + a door app + a bar POS + a spreadsheet with one platform.",
                  "Ticketing de eventos, check-in QR en puerta, bar tabs en vivo, VIP table map y links de promotores. Reemplaza un sitio de tickets + app de puerta + POS de bar + spreadsheet con una plataforma."
                )}
              </p>

              {/* Tag pills */}
              <div className="flex flex-wrap gap-2 mb-8">
                {[
                  { en: "Event ticketing", es: "Ticketing" },
                  { en: "QR door", es: "Puerta QR" },
                  { en: "VIP map", es: "VIP map" },
                  { en: "Bar tabs", es: "Bar tabs" },
                  { en: "6 features", es: "6 features" },
                ].map((tag) => (
                  <span
                    key={tag.en}
                    className="text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full border"
                    style={{
                      backgroundColor: "rgba(124, 58, 237, 0.12)",
                      color: "#C4B5FD",
                      borderColor: "rgba(124, 58, 237, 0.25)",
                    }}
                  >
                    {t(tag.en, tag.es)}
                  </span>
                ))}
              </div>

              <Link
                href="/solution/nightlife"
                className="inline-flex items-center gap-2 text-[14px] font-bold text-white px-6 py-3 rounded-full hover:scale-[1.02] transition-all shadow-lg"
                style={{
                  backgroundImage:
                    "linear-gradient(135deg, #7C3AED 0%, #F472B6 100%)",
                  boxShadow: "0 10px 30px rgba(124, 58, 237, 0.35)",
                }}
              >
                {t("Explore nightlife", "Ver nightlife")}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </AnimatedElement>
        </div>

        {/* Comparison table */}
        <AnimatedSection delay={0.2}>
          <div className="bg-white/[0.03] border border-white/10 rounded-[32px] overflow-hidden">
            {/* ── Desktop layout (md+): 3-column grid ─────────────────────── */}
            <div className="hidden md:block">
              <div className="grid grid-cols-12 gap-4 px-6 md:px-10 py-5 border-b border-white/10 bg-white/[0.02]">
                <div className="col-span-4">
                  <div className="text-[10px] font-bold tracking-widest uppercase text-white/40">
                    {t("Side by side", "Lado a lado")}
                  </div>
                </div>
                <div className="col-span-4">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-redy-orange/15 flex items-center justify-center">
                      <Utensils className="w-3.5 h-3.5 text-redy-orange" />
                    </div>
                    <div className="text-[12px] font-black uppercase tracking-wider text-redy-orange">
                      {t("Restaurant", "Restaurante")}
                    </div>
                  </div>
                </div>
                <div className="col-span-4">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-[#7C3AED]/20 flex items-center justify-center">
                      <Wine className="w-3.5 h-3.5" style={{ color: "#C4B5FD" }} />
                    </div>
                    <div
                      className="text-[12px] font-black uppercase tracking-wider"
                      style={{ color: "#F472B6" }}
                    >
                      {t("Nightlife", "Nightlife")}
                    </div>
                  </div>
                </div>
              </div>

              <div className="divide-y divide-white/5">
                {ROWS.map((row) => (
                  <div
                    key={row.label.en}
                    className="grid grid-cols-12 gap-4 px-6 md:px-10 py-5 items-center hover:bg-white/[0.02] transition-colors"
                  >
                    <div className="col-span-4">
                      <div className="text-[13px] font-bold text-white/55">
                        {t(row.label.en, row.label.es)}
                      </div>
                    </div>
                    <div className="col-span-4">
                      <Cell value={row.restaurant} tone="restaurant" />
                    </div>
                    <div className="col-span-4">
                      <Cell value={row.nightlife} tone="nightlife" />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* ── Mobile layout (<md): stacked per row ────────────────────── */}
            <div className="md:hidden">
              <div className="px-5 py-4 border-b border-white/10 bg-white/[0.02]">
                <div className="text-[10px] font-bold tracking-widest uppercase text-white/40">
                  {t("Side by side", "Lado a lado")}
                </div>
              </div>
              <div className="divide-y divide-white/5">
                {ROWS.map((row) => (
                  <div key={row.label.en} className="px-5 py-5">
                    <div className="text-[11px] font-black uppercase tracking-wider text-white/45 mb-3">
                      {t(row.label.en, row.label.es)}
                    </div>
                    <div className="space-y-2.5">
                      <div className="flex items-start gap-3 bg-white/[0.03] border border-white/5 rounded-[14px] px-3.5 py-3">
                        <div className="w-6 h-6 rounded-md bg-redy-orange/15 flex items-center justify-center shrink-0 mt-0.5">
                          <Utensils className="w-3 h-3 text-redy-orange" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[10px] font-black uppercase tracking-wider text-redy-orange mb-1">
                            {t("Restaurant", "Restaurante")}
                          </div>
                          <Cell value={row.restaurant} tone="restaurant" />
                        </div>
                      </div>
                      <div className="flex items-start gap-3 bg-white/[0.03] border border-white/5 rounded-[14px] px-3.5 py-3">
                        <div className="w-6 h-6 rounded-md bg-[#7C3AED]/20 flex items-center justify-center shrink-0 mt-0.5">
                          <Wine className="w-3 h-3" style={{ color: "#C4B5FD" }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[10px] font-black uppercase tracking-wider mb-1" style={{ color: "#F472B6" }}>
                            {t("Nightlife", "Nightlife")}
                          </div>
                          <Cell value={row.nightlife} tone="nightlife" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer note */}
            <div className="px-6 md:px-10 py-5 border-t border-white/10 bg-white/[0.02]">
              <p className="text-[12px] font-medium text-white/45 leading-relaxed">
                {t(
                  "Same platform under the hood. Different product on top. Pick the one that fits how you actually run.",
                  "Misma plataforma por debajo. Distinto producto por encima. Elige el que se acomoda a cómo realmente operas."
                )}
              </p>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
}
